import React from 'react'
import type { DemoForkInfo } from '../types'
import { Loader2, Share, PlayCircle, ChevronRight, Check, Palette, Image, Undo2, Redo2, ArrowLeft, GitFork } from 'lucide-react'

interface EditorHeaderProps {
  title: string
  saving: boolean
  published: boolean
  publishing: boolean
  canUndo: boolean
  canRedo: boolean
  showDesignPanel: 'background' | 'theme' | null
  forkedFrom?: DemoForkInfo
  isGuest?: boolean
  onUndo: () => void
  onRedo: () => void
  onShare: () => void
  onPreview: () => void
  onPublish: () => void
  onToggleDesignPanel: (panel: 'background' | 'theme' | null) => void
}

export default function EditorHeader({
  title, saving, published, publishing,
  canUndo, canRedo, showDesignPanel, forkedFrom, isGuest,
  onUndo, onRedo, onShare, onPreview, onPublish, onToggleDesignPanel,
}: EditorHeaderProps) {
  return (
    <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3 shrink-0">
      <div className="flex items-center gap-3">
        <a
          href="/dashboard"
          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
          title="Back to dashboard"
        >
          <ArrowLeft className="h-4 w-4" />
        </a>
        <img src="/logo-dark.png" alt="Explaina" className="h-7" />
        <div className="h-5 w-[1px] bg-slate-200"></div>
        <div className="flex flex-col">
          <div className="flex items-center gap-1 text-xs text-slate-500 font-medium">
            <a href="/dashboard" className="hover:text-slate-700 transition-colors">Projects</a>
            <ChevronRight className="h-3 w-3" />
            <span className="text-slate-900 font-semibold">{title}</span>
          </div>
          {forkedFrom && (
            <div className="flex items-center gap-1 text-[10px] text-slate-400 mt-0.5">
              <GitFork className="h-3 w-3" />
              <span>Remixed from</span>
              {forkedFrom.published ? (
                <a href={`/d/${forkedFrom.slug}`} className="text-[#5048e5] hover:underline font-medium" target="_blank" rel="noopener noreferrer">
                  {forkedFrom.title}
                </a>
              ) : (
                <span className="text-slate-500">{forkedFrom.title}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Center toolbar: Undo/Redo + Design */}
      <div className="hidden md:flex items-center gap-1">
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1">
          <button
            onClick={onUndo}
            disabled={!canUndo}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-slate-500 hover:text-slate-700 hover:bg-white rounded-md text-xs font-medium transition-all disabled:opacity-30 disabled:hover:bg-transparent"
            title="Undo (Ctrl+Z)"
          >
            <Undo2 className="h-4 w-4" />
            Undo
          </button>
          <button
            onClick={onRedo}
            disabled={!canRedo}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-slate-500 hover:text-slate-700 hover:bg-white rounded-md text-xs font-medium transition-all disabled:opacity-30 disabled:hover:bg-transparent"
            title="Redo (Ctrl+Shift+Z)"
          >
            <Redo2 className="h-4 w-4" />
            Redo
          </button>
        </div>
        <div className="h-5 w-[1px] bg-slate-200 mx-1.5"></div>
        <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => onToggleDesignPanel(showDesignPanel === 'background' ? null : 'background')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
              showDesignPanel === 'background'
                ? 'bg-[#5048e5] text-white'
                : 'text-slate-500 hover:text-slate-700 hover:bg-white'
            }`}
            title="Background"
          >
            <Image className="h-4 w-4" />
            Background
          </button>
          <button
            onClick={() => onToggleDesignPanel(showDesignPanel === 'theme' ? null : 'theme')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
              showDesignPanel === 'theme'
                ? 'bg-[#5048e5] text-white'
                : 'text-slate-500 hover:text-slate-700 hover:bg-white'
            }`}
            title="Theme"
          >
            <Palette className="h-4 w-4" />
            Theme
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {saving && (
          <span className="flex items-center gap-1 text-xs text-slate-400 italic">
            <Loader2 className="h-3 w-3 animate-spin" /> Saving...
          </span>
        )}
        {!saving && (
          <span className="text-xs text-slate-400 italic">Auto-saved</span>
        )}
        {!isGuest && published && (
          <>
            <button
              onClick={onShare}
              className="flex items-center gap-2 px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
            >
              <Share className="h-4 w-4" />
              <span className="text-sm font-medium">Share</span>
            </button>
            <div className="h-6 w-[1px] bg-slate-200 mx-1"></div>
            <button
              onClick={onPreview}
              className="flex items-center gap-1.5 px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
              title="View published demo"
            >
              <PlayCircle className="h-4 w-4" />
              <span className="text-sm font-medium">View live</span>
            </button>
          </>
        )}
        {published ? (
          <button
            disabled
            className="flex items-center gap-1.5 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm opacity-90 cursor-default"
          >
            <Check className="h-4 w-4" />
            Published
          </button>
        ) : isGuest ? (
          <button
            data-auth-open="signup"
            data-auth-intended={window.location.pathname}
            className="bg-[#5048e5] text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm shadow-[#5048e5]/25 hover:bg-[#5048e5]/90 transition-all"
          >
            Sign up to publish
          </button>
        ) : (
          <button
            onClick={onPublish}
            className="bg-[#5048e5] text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm shadow-[#5048e5]/25 hover:bg-[#5048e5]/90 transition-all"
          >
            Publish
          </button>
        )}
      </div>
    </header>
  )
}
